// YERİ: src/app/domains/website/core/site-analytics.tracker.ts
//
// First-party, COOKIELESS visitor analytics of the platform storefront
// (melbarrel.com "vitrin"). Backend: api/web/analytics (SiteAnalyticsController).
//
// ── WHAT IT SENDS ──────────────────────────────────────────────────────────
// • pageview on every router NavigationEnd: path (NO query string), title,
//   browser language; on the session's first page view also the external
//   referrer and utm_source/medium/campaign parsed from the landing URL.
// • ping every 30 s while the tab is visible (keeps the "live now" card honest).
//
// ── PRIVACY ────────────────────────────────────────────────────────────────
// • No cookie, no localStorage. The session id is a random UUID in
//   sessionStorage (dies with the tab); a new one after 30 min of inactivity.
// • The server never stores the IP: it only feeds a daily-rotating hash.
// • Do Not Track / Global Privacy Control → nothing is sent at all.
// • No consent banner dependency: no personal data, no cookie (the GA consent
//   banner is a separate concern and is not read here).
//
// ── WHERE IT RUNS ──────────────────────────────────────────────────────────
// Browser only (never during SSR/prerender) and only on the PLATFORM host —
// tenant storefronts share the Angular app but are not this report's subject.
// Started by the storefront shell (layout/web-layout.ts), stopped when it is
// destroyed (e.g. navigating into /admin).

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { API_BASE } from '@/app/core/auth/auth.service';
import { TenantHostService } from '@/app/core/tenant-host/tenant-host.service';

const STORAGE_KEY = 'mb.siteAnalytics.session';
const SESSION_IDLE_MS = 30 * 60 * 1000;
const HEARTBEAT_MS = 30 * 1000;
/** Title is set by the page after its data loads; wait a moment before reading it. */
const PAGEVIEW_DELAY_MS = 800;
const EXCLUDED_PREFIXES = ['/admin', '/portal', '/api', '/member', '/inspector'];

interface StoredSession {
  id: string;
  /** Epoch ms of the last event. */
  last: number;
  /** First page view already sent (referrer/utm go only with the first one). */
  started: boolean;
}

interface PageViewPayload {
  sid: string;
  path: string;
  title?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  lang?: string;
}

@Injectable({ providedIn: 'root' })
export class SiteAnalyticsTracker {
  private router = inject(Router);
  private doc = inject(DOCUMENT);
  private host = inject(TenantHostService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private navSub: Subscription | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private pending: ReturnType<typeof setTimeout> | null = null;
  private pendingFlush: (() => void) | null = null;
  private currentPath: string | null = null;
  /** In-memory fallback when sessionStorage is unavailable (privacy mode, sandboxed iframe). */
  private memorySession: StoredSession | null = null;

  private readonly onVisibility = () => {
    if (this.doc.visibilityState === 'visible') this.ping();
  };
  private readonly onPageHide = () => this.flushPending();

  /** Idempotent. No-op on the server, on tenant hosts and when the visitor opted out. */
  start(): void {
    if (this.navSub || !this.enabled()) return;

    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.onNavigation(e.urlAfterRedirects));

    this.heartbeat = setInterval(() => {
      if (this.doc.visibilityState === 'visible') this.ping();
    }, HEARTBEAT_MS);
    this.doc.addEventListener('visibilitychange', this.onVisibility);
    this.doc.defaultView?.addEventListener('pagehide', this.onPageHide);
  }

  stop(): void {
    this.flushPending();
    this.navSub?.unsubscribe();
    this.navSub = null;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    this.doc.removeEventListener('visibilitychange', this.onVisibility);
    this.doc.defaultView?.removeEventListener('pagehide', this.onPageHide);
    this.currentPath = null;
  }

  // ── Events ─────────────────────────────────────────────────────────────

  private onNavigation(url: string): void {
    const path = this.cleanPath(url);
    if (!path) return;
    // A previous page view still waiting for its title goes out now.
    this.flushPending();
    this.currentPath = path;

    const send = () => {
      this.pending = null;
      this.pendingFlush = null;
      this.sendPageView(path);
    };
    this.pendingFlush = send;
    this.pending = setTimeout(send, PAGEVIEW_DELAY_MS);
  }

  private flushPending(): void {
    if (this.pending) clearTimeout(this.pending);
    this.pending = null;
    const fn = this.pendingFlush;
    this.pendingFlush = null;
    fn?.();
  }

  private sendPageView(path: string): void {
    const session = this.session();
    const payload: PageViewPayload = {
      sid: session.id,
      path,
      title: (this.doc.title || '').slice(0, 200) || undefined,
      lang: this.doc.defaultView?.navigator.language || undefined,
    };

    if (!session.started) {
      const referrer = this.externalReferrer();
      if (referrer) payload.referrer = referrer;
      const params = this.landingParams();
      payload.utmSource = params?.get('utm_source') ?? undefined;
      payload.utmMedium = params?.get('utm_medium') ?? undefined;
      payload.utmCampaign = params?.get('utm_campaign') ?? undefined;
      session.started = true;
    }
    this.save(session);
    this.post('pageview', payload);
  }

  private ping(): void {
    if (!this.currentPath) return;
    const session = this.session();
    // The session expired while the tab sat idle: the next navigation opens a
    // new one; a ping for a session the server never saw would be dropped anyway.
    if (!session.started) return;
    this.save(session);
    this.post('ping', { sid: session.id, path: this.currentPath });
  }

  // ── Transport ──────────────────────────────────────────────────────────

  private post(kind: 'pageview' | 'ping', body: object): void {
    const url = `${API_BASE}/web/analytics/${kind}`;
    const json = JSON.stringify(body);
    try {
      const nav = this.doc.defaultView?.navigator;
      if (nav?.sendBeacon && nav.sendBeacon(url, new Blob([json], { type: 'application/json' }))) return;
      void fetch(url, {
        method: 'POST',
        body: json,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        credentials: 'omit',
      }).catch(() => undefined);
    } catch {
      // Analytics must never break the page.
    }
  }

  // ── Session (sessionStorage, 30 min idle timeout) ──────────────────────

  private session(): StoredSession {
    const now = Date.now();
    let s = this.load();
    if (!s || now - s.last > SESSION_IDLE_MS) {
      s = { id: this.uuid(), last: now, started: false };
    }
    s.last = now;
    return s;
  }

  private load(): StoredSession | null {
    try {
      const raw = this.doc.defaultView?.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return this.memorySession;
      const parsed = JSON.parse(raw) as Partial<StoredSession>;
      if (typeof parsed.id !== 'string' || typeof parsed.last !== 'number') return null;
      return { id: parsed.id, last: parsed.last, started: parsed.started === true };
    } catch {
      return this.memorySession;
    }
  }

  private save(s: StoredSession): void {
    this.memorySession = s;
    try {
      this.doc.defaultView?.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch {
      // Storage blocked — the in-memory copy keeps the session for this page life.
    }
  }

  private uuid(): string {
    const c = this.doc.defaultView?.crypto;
    if (c?.randomUUID) return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (c?.getRandomValues) c.getRandomValues(bytes);
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // ── Guards and helpers ─────────────────────────────────────────────────

  private enabled(): boolean {
    if (!this.isBrowser || this.host.kind !== 'platform') return false;
    const nav = this.doc.defaultView?.navigator as (Navigator & { globalPrivacyControl?: boolean }) | undefined;
    if (!nav) return false;
    if (nav.doNotTrack === '1' || nav.globalPrivacyControl === true) return false;
    // Headless/automation browsers identify themselves here; the server filters UAs too.
    if (nav.webdriver) return false;
    return true;
  }

  /** Router URL → path only (query/fragment dropped); null for non-storefront surfaces. */
  private cleanPath(url: string): string | null {
    let p = url.split(/[?#]/)[0] || '/';
    if (p.length > 1) p = p.replace(/\/+$/, '') || '/';
    const lower = p.toLowerCase();
    if (EXCLUDED_PREFIXES.some((x) => lower === x || lower.startsWith(x + '/'))) return null;
    return p.slice(0, 300);
  }

  /** document.referrer only when it comes from ANOTHER site (internal hops are not a source). */
  private externalReferrer(): string | null {
    const ref = this.doc.referrer;
    if (!ref) return null;
    try {
      const u = new URL(ref);
      if (u.host === this.doc.location?.host) return null;
      // Only the origin travels: the referrer's path/query may carry personal data.
      return u.origin;
    } catch {
      return null;
    }
  }

  /**
   * Query of the URL the document was LOADED with. `location.search` is not
   * enough: langGuard redirects '/?utm_source=x' to '/en' and drops the query
   * before the storefront shell (and this tracker) even exists. The navigation
   * timing entry keeps the original document URL.
   */
  private landingParams(): URLSearchParams | null {
    try {
      const entry = this.doc.defaultView?.performance
        ?.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
      const search = entry?.name ? new URL(entry.name).search : '';
      return new URLSearchParams(search || this.doc.location?.search || '');
    } catch {
      return null;
    }
  }
}
